import { FC } from 'react';
import { Box, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useAutofixStyles } from './styles';
import { YardstickAutoFix } from '@internal/yardstick-common';
import { AutofixV2RecommendationFlow } from './autofix-v2-recommendation-flow';
import { AutofixV2SingleAutoFixFlow } from './autofix-v2-single-autofix-flow';

interface AutofixV2ComponentProps {
  issueId?: string;
  autofixConfig?: YardstickAutoFix;
  remediationSteps?: string;
  onClose?: () => void;
  entityRef?: string;
  checkId?: string;
  isGroupedIssue?: boolean; // Flag for grouped issues in logical accordion
  forceExpandPreview?: boolean; // Force preview to be expanded (no collapse option)
  issueIds?: string[]; // NEW: Array of issue IDs for bulk execution (resource-grouped)
  isResourceGrouped?: boolean; // NEW: Flag indicating resource-grouped execution
}

/**
 * AutoFix V2 Component - Orchestrator
 *
 * This component determines which flow pattern to use based on configuration:
 * - Pattern 1: Recommendation + AutoFix (multi-step with review)
 * - Pattern 2: Simple AutoFix (single step, no recommendation)
 *
 * Pattern 3 (multi-step autofix only) has been removed for simplicity.
 *
 * Resource-Grouped Support:
 * - When `issueIds` is provided with multiple IDs, enables bulk execution
 * - All issues in the group share the same autofix configuration
 * - Displays resource count to user
 * - Backend executes handler once for all resources
 */
export const AutofixV2Component: FC<AutofixV2ComponentProps> = ({
  issueId,
  autofixConfig,
  remediationSteps,
  onClose,
  entityRef,
  checkId,
  isGroupedIssue = false,
  forceExpandPreview = false,
  issueIds,
  isResourceGrouped = false,
}) => {
  const classes = useAutofixStyles();

  // Calculate resource count for bulk execution
  const resourceCount = issueIds?.length || 1;
  const isBulkExecution = resourceCount > 1;

  // Validate configuration exists
  if (
    !autofixConfig ||
    !autofixConfig.config ||
    autofixConfig.config.length === 0
  ) {
    return (
      <Box className={classes.container}>
        <Alert severity="info">
          AutoFix V2 is not configured for this check. Please ensure the check
          has proper AutoFix metadata.
        </Alert>
      </Box>
    );
  }

  // Determine which pattern to use
  const recommendationFlow =
    autofixConfig.strategy === 'recommendation-autofix';
  const simpleAutofixFlow = autofixConfig.strategy === 'sequential';
  const singleStepAutofix =
    simpleAutofixFlow && autofixConfig.config.length === 1;

  // Display resource count for bulk execution
  const resourceCountDisplay = isBulkExecution && isResourceGrouped && (
    <Box className={classes.resourceCountBox}>
      <Typography variant="body2" color="textSecondary">
        This autofix will be applied to{' '}
        <strong>{resourceCount} resources</strong>.
      </Typography>
    </Box>
  );

  // Pattern 1: Recommendation + AutoFix flow
  if (recommendationFlow) {
    const recommendationStep = autofixConfig.config.find(
      s => s.type === 'recommendation',
    );
    const autofixStep = autofixConfig.config.find(s => s.type === 'autofix');

    if (!recommendationStep || !autofixStep) {
      return (
        <Box className={classes.container}>
          <Alert severity="error">
            Invalid configuration: Recommendation flow requires both
            recommendation and autofix steps.
          </Alert>
        </Box>
      );
    }

    return (
      <Box>
        {resourceCountDisplay}
        <AutofixV2RecommendationFlow
          issueId={issueId || ''}
          recommendationStep={recommendationStep}
          autofixStep={autofixStep}
          onClose={onClose}
          entityRef={entityRef}
          checkId={checkId}
          isGroupedIssue={isGroupedIssue}
          forceExpandPreview={forceExpandPreview}
          issueIds={issueIds}
          _isResourceGrouped={isResourceGrouped}
        />
      </Box>
    );
  }

  // Pattern 2: Simple AutoFix flow (single step only)
  if (singleStepAutofix) {
    return (
      <Box>
        {resourceCountDisplay}
        <AutofixV2SingleAutoFixFlow
          issueId={issueId || ''}
          autofixStep={autofixConfig.config[0]}
          remediationSteps={remediationSteps}
          onClose={onClose}
          entityRef={entityRef}
          checkId={checkId}
          isGroupedIssue={isGroupedIssue}
          forceExpandPreview={forceExpandPreview}
          issueIds={issueIds}
          _isResourceGrouped={isResourceGrouped}
        />
      </Box>
    );
  }

  // Pattern 3 (multi-step autofix only) is no longer supported
  return (
    <Box className={classes.container}>
      <Alert severity="warning">
        Multiple autofix steps without recommendations are not supported. Please
        configure either a single autofix step or a recommendation + autofix
        flow.
      </Alert>
    </Box>
  );
};

export default AutofixV2Component;
